import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, Plus, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  NOTE_TYPES,
  noteTypeBadgeClass,
  formatNoteDate,
} from '@/lib/jobPhases';
import { addJobNote } from '@/lib/jobsData';
import { translateNoteType } from '@/lib/i18nLabels';
import { toast } from 'sonner';

const PUBLIC_NOTE_TYPES = NOTE_TYPES.filter((n) => n.key !== 'field');

export const JobNotesTab = ({ job, onUpdated }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [type, setType] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!text.trim()) {
      setError(t('jobs.notes.error_required'));
      return;
    }
    setSubmitting(true);
    try {
      const updated = await addJobNote(job, { text, type });
      toast.success(t('jobs.notes.added_toast'));
      onUpdated?.(updated);
      setText('');
      setType('general');
    } catch (err) {
      setError(err?.message || t('jobs.notes.add_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Newest first — we don't have authoritative timestamps so show in reverse insertion order.
  const notes = Array.isArray(job?.notes) ? [...job.notes].reverse() : [];

  return (
    <div className="space-y-4" data-testid="job-notes-tab">
      {/* Add note form */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobs.notes.add_field_note')}
              </Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger
                  className="h-9 w-[140px] bg-[hsl(var(--muted))] border-border text-xs"
                  data-testid="note-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  {PUBLIC_NOTE_TYPES.map((nt) => (
                    <SelectItem key={nt.key} value={nt.key}>
                      {translateNoteType(t, nt.key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('jobs.notes.placeholder')}
              className="bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 resize-none"
              data-testid="note-text"
            />
            {error ? (
              <Alert
                className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                data-testid="note-error"
              >
                <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex items-center justify-end">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                data-testid="note-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saving_short')}
                  </>
                ) : (
                  <>
                    <Plus size={14} className="mr-1.5" />
                    {t('jobs.notes.add_note')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <StickyNote size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('jobs.notes.notes_label', { count: notes.length })}
            </div>
          </div>
          {notes.length === 0 ? (
            <div className="mt-3 text-sm text-muted-foreground">
              {t('jobs.notes.empty')}
            </div>
          ) : (
            <ul className="mt-3 space-y-2.5" data-testid="notes-list">
              {notes.map((note, i) => (
                <li
                  key={`${i}-${(note?.text || '').slice(0, 16)}`}
                  className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3.5 py-3"
                  data-testid={`note-item-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={cn(
                        'uppercase text-[10px] tracking-[0.16em]',
                        noteTypeBadgeClass(note?.type)
                      )}
                    >
                      {translateNoteType(t, note?.type)}
                    </Badge>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {formatNoteDate(note?.date)}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground/95 leading-snug whitespace-pre-line">
                    {note?.text || ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobNotesTab;
